import {
  GetOpportunitiesSortKey,
  Opportunity,
  SortDirection,
  UserPermissions,
} from "generated/graphql";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import OpportunityActionsCell from "modules/Opportunities/OpportunityActionsCell/OpportunityActionsCell";
import { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { Cell } from "react-table";
import useGetOpportunities from "./useGetOpportunities";
import { Link } from "react-router-dom";
import { sortDirectionMap } from "constants/sortDirectionMap";
import MultipleChoiceFilter from "components/filters/MultipleChoiceFilter/MultipleChoiceFilter";
import useGetAllOrganizationUsersNonEmployee from "hooks/users/useGetAllOrganizationUsersNonEmployee";
import { CustomGetCompaniesForFilterDocument } from "generated/graphql";
import usePaginatedCompanyLookup from "hooks/companies/usePaginatedCompanyLookup";
import DateFilter from "components/filters/DateFilter/DateFilter";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { IconButton, InputBase, Paper } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { capitalCase } from "change-case";
import useGetOpportunityStatuses from "hooks/opportunities/useGetOpportunityStatuses";
import formatAsMMDDYYYY from "utils/dates/formatAsMMDDYYYY";
import truncateWithEllipsis from "utils/strings/truncateWithEllipsis";
import { useBitumioContext } from "providers/BitumioContext";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";

export const opportunitiesSortKeyMap = {
  id: GetOpportunitiesSortKey.ID,
  name: GetOpportunitiesSortKey.NAME,
  addressLine1: GetOpportunitiesSortKey.ADDRESS,
  addressCity: GetOpportunitiesSortKey.ADDRESS,
  contact: GetOpportunitiesSortKey.CONTACT,
  company: GetOpportunitiesSortKey.COMPANY,
  status: GetOpportunitiesSortKey.STATUS,
  "company.name": GetOpportunitiesSortKey.COMPANY,
  "contact.firstName": GetOpportunitiesSortKey.CONTACT,
  dueAt: GetOpportunitiesSortKey.DUEAT,
};

export const opportunitiesInitialSortDirectionKey = "asc";
export const opportunitiesInitialSortDirectionValue = SortDirection.ASC;
export const opportunitiesInitialSortKeyKey = "id";
export const opportunitiesInitialSortKeyValue = GetOpportunitiesSortKey.ID;

const Search = ({ defaultValue, onQueryChange }) => {
  return (
    <MDBox position="relative" zIndex={2}>
      <MDBox display="flex" alignItems="center" flexWrap="wrap">
        <Paper
          className="tour-search-opportunities"
          component="form"
          sx={{ p: "2px 4px", display: "flex", alignItems: "center", width: 400, mr: 2 }}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <InputBase
            autoFocus
            sx={{ ml: 1, flex: 1, fontSize: "14px" }}
            placeholder="Search Opportunities"
            value={defaultValue}
            inputProps={{ "aria-label": "search opportunities" }}
            onChange={({ currentTarget }: any) => {
              onQueryChange(currentTarget.value);
            }}
          />
          <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
            <SearchIcon />
          </IconButton>
        </Paper>
      </MDBox>
    </MDBox>
  );
};

export default function useOpportunitiesTable({
  companyId,
}: {
  companyId?: string;
} = {}) {
  const [{ signedInUser }] = useBitumioContext();
  const { hasPermissions } = useCheckPermissions();
  const canViewAll = hasPermissions(UserPermissions.VIEW_OPPORTUNITIES);
  const isAssignedOnly =
    !canViewAll && hasPermissions(UserPermissions.VIEW_ONLY_ASSIGNED_OPPORTUNITIES);
  const filtersStorageKey = `useOpportunitiesTableFilters:v2:${signedInUser?.id ?? "anonymous"}`;
  let savedFilters = JSON.parse(localStorage.getItem(filtersStorageKey)) ?? null;
  if (companyId) {
    // For companies view
    savedFilters = {};
  }

  // Local state for createdAt filter (until backend support is added)
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const { filtering, sorting, offset } = usePaginatedVariables({
    initialCompanyId: companyId ?? null,
    initialStatuses: savedFilters?.statuses,
    initialMultipleCompanyIds: savedFilters?.multipleCompanyIDs,
    initialDueAt: savedFilters?.dueAt,
    initialSearchTerm: savedFilters?.searchTerm ?? null,
    initialMultipleUserIDs:
      isAssignedOnly || companyId || canViewAll ? null : savedFilters?.multipleUserIDs ?? null,
    initialUserID: isAssignedOnly ? signedInUser?.id ?? null : null,
  });

  const params = {
    page: offset.page,
    first: offset.first,
    search: filtering.debouncedSearchTerm,
    sort: sortDirectionMap[sorting.sortDirection],
    sortKey: opportunitiesSortKeyMap[sorting.sortKey] || opportunitiesInitialSortKeyValue,
    sortDirection:
      sortDirectionMap[sorting.sortDirection] || opportunitiesInitialSortDirectionValue,
    statuses: filtering.statuses,
    dueAt: filtering.dueAt,
    userId: isAssignedOnly ? signedInUser?.id ?? null : filtering.userID,
    companyId: filtering.companyId,
    multipleUserIDs: isAssignedOnly ? [] : filtering.multipleUserIDs,
    multipleCompanyIDs: filtering.multipleCompanyIDs,
  };

  useEffect(() => {
    if (!isAssignedOnly) {
      return;
    }

    if (filtering.userID !== (signedInUser?.id ?? null)) {
      filtering.setUserID(signedInUser?.id ?? null);
    }

    if (filtering.multipleUserIDs.length > 0) {
      filtering.setMultipleUserIDs([]);
    }
  }, [
    filtering.multipleUserIDs,
    filtering.setMultipleUserIDs,
    filtering.setUserID,
    filtering.userID,
    isAssignedOnly,
    signedInUser?.id,
  ]);

  useEffect(() => {
    localStorage.setItem(filtersStorageKey, JSON.stringify(filtering));
  }, [filtersStorageKey, filtering]);

  const { data, loading, error, paginatorInfo, refetch } = useGetOpportunities(params);
  const {
    loading: opportunityStatusesLoading,
    data: opportunityStatuses,
    error: opportunityStatusesError,
  } = useGetOpportunityStatuses();
  const {
    loading: organizationUsersLoading,
    data: organizationUsersData,
    error: organizationUsersError,
  } = useGetAllOrganizationUsersNonEmployee();

  const {
    loading: companiesLoading,
    items: companiesData,
    error: companiesError,
    search: searchCompanies,
    loadInitialPage: loadInitialCompanies,
    loadMore: loadMoreCompanies,
    resetSearch: resetSearchCompanies,
    hasMorePages: companiesHasMorePages,
  } = usePaginatedCompanyLookup<{ id: string; name: string }>(
    CustomGetCompaniesForFilterDocument,
    filtering.multipleCompanyIDs ?? []
  );

  const pagination = usePaginatedResources({
    paginate: offset.paginate,
    paginatorInfo,
  });

  const searchControl = (
    <Search defaultValue={filtering.searchTerm} onQueryChange={filtering.setSearchTerm} />
  );

  const handleStatusChecked = useCallback(
    (selectedOptions) => {
      return filtering.setStatuses(selectedOptions);
    },
    [filtering]
  );

  const statusFilterOptions = useMemo(
    () =>
      opportunityStatuses?.map((status) => ({
        label: capitalCase(status.name),
        value: status.name,
      })) ?? [],
    [opportunityStatuses]
  );

  const StatusFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <MultipleChoiceFilter
          id={id}
          value={filtering.statuses}
          options={statusFilterOptions}
          loading={opportunityStatusesLoading}
          error={opportunityStatusesError}
          onChange={handleStatusChecked}
        />
      );
    },
    [
      filtering.statuses,
      handleStatusChecked,
      opportunityStatusesError,
      opportunityStatusesLoading,
      statusFilterOptions,
    ]
  );

  const handleSalesPersonChecked = useCallback(
    (selectedOptions) => {
      return filtering.setMultipleUserIDs(selectedOptions);
    },
    [filtering]
  );

  const handleClientChecked = useCallback(
    (selectedOptions) => {
      return filtering.setMultipleCompanyIds(selectedOptions);
    },
    [filtering]
  );

  const SalesPersonFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <MultipleChoiceFilter
          id={id}
          value={filtering.multipleUserIDs}
          loading={organizationUsersLoading}
          error={organizationUsersError}
          options={
            organizationUsersData?.map((user) => ({
              label: user.name,
              value: user.id,
            })) ?? []
          }
          onChange={handleSalesPersonChecked}
        />
      );
    },
    [
      organizationUsersData,
      filtering.multipleUserIDs,
      organizationUsersLoading,
      organizationUsersError,
    ]
  );

  const companyFilterProps = useRef({
    value: filtering.multipleCompanyIDs,
    loading: companiesLoading,
    error: companiesError,
    search: searchCompanies,
    onOpen: loadInitialCompanies,
    onClose: resetSearchCompanies,
    onEndReached: loadMoreCompanies,
    hasMorePages: companiesHasMorePages,
    options: companiesData?.map((company) => ({ label: company.name, value: company.id })) ?? [],
    onChange: handleClientChecked,
  });
  companyFilterProps.current = {
    value: filtering.multipleCompanyIDs,
    loading: companiesLoading,
    error: companiesError,
    search: searchCompanies,
    onOpen: loadInitialCompanies,
    onClose: resetSearchCompanies,
    onEndReached: loadMoreCompanies,
    hasMorePages: companiesHasMorePages,
    options: companiesData?.map((company) => ({ label: company.name, value: company.id })) ?? [],
    onChange: handleClientChecked,
  };

  const CompanyFilter = useCallback(() => {
    return <MultipleChoiceFilter id="company.name" {...companyFilterProps.current} />;
  }, []);

  const DueAtFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <DateFilter
          id={id}
          value={filtering.dueAt}
          onChange={(selectedDate) =>
            filtering.setDueAt((prevState) => (prevState == selectedDate ? null : selectedDate))
          }
        />
      );
    },
    [filtering.dueAt]
  );

  const CreatedAtFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <DateFilter
          id={id}
          value={createdAt}
          onChange={(selectedDate) =>
            setCreatedAt((prevState) => (prevState == selectedDate ? null : selectedDate))
          }
        />
      );
    },
    [createdAt]
  );

  const columns = useMemo(() => {
    const compactText = (value: string | number | null | undefined) => (
      <MDTypography variant="caption" sx={{ fontSize: "0.72rem", lineHeight: 1.2 }}>
        {value ?? ""}
      </MDTypography>
    );

    return [
      {
        Header: "ID",
        accessor: "id",
        Cell: ({ value }) => (
          <Link style={{ color: "inherit" }} to={`/opportunities/${value}/update`}>
            {compactText(value)}
          </Link>
        ),
        width: "56px",
      },
      {
        Header: "Created Date",
        accessor: "createdAt",
        Filter: CreatedAtFilter,
        Cell: ({ value }) => compactText(formatAsMMDDYYYY(value)),
        width: "110px",
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ row, value }: Cell<Opportunity>) => (
          <Link
            style={{ color: "inherit", display: "block", maxWidth: "220px" }}
            to={`/opportunities/${row.original.id}/update`}
            title={value}
          >
            {compactText(truncateWithEllipsis(value, 30))}
          </Link>
        ),
        width: "220px",
      },
      {
        Header: "Status",
        accessor: "status",
        Filter: StatusFilter,
        Cell: ({ value }) => compactText(capitalCase(value)),
      },
      {
        Header: "Client",
        accessor: "company.name",
        Filter: CompanyFilter,
        Cell: ({ value }) => compactText(value),
      },
      {
        Header: "City",
        accessor: "addressCity",
        Cell: ({ value }) => compactText(value),
      },
      {
        Header: "Due Date",
        accessor: "dueAt",
        Filter: DueAtFilter,
        Cell: ({ value }) => compactText(formatAsMMDDYYYY(value)),
        width: "110px",
      },
      ...(!isAssignedOnly
        ? [
            {
              Header: "Sales Person",
              accessor: "user.firstName",
              Cell: ({ row, value }: Cell<Opportunity>) =>
                compactText(
                  row.original.user
                    ? row.original.user.firstName + " " + row.original.user.lastName
                    : ""
                ),
              disableSortBy: true,
              Filter: SalesPersonFilter,
            },
          ]
        : []),
      {
        align: "center",
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: OpportunityActionsCell,
        width: "84px",
      },
    ];
  }, [
    StatusFilter,
    SalesPersonFilter,
    CompanyFilter,
    DueAtFilter,
    CreatedAtFilter,
    isAssignedOnly,
  ]);

  const tableData = useMemo(() => {
    return { columns, rows: data };
  }, [columns, data]);

  return {
    data,
    loading,
    error,
    columns,
    tableData,
    pagination,
    filtering,
    sorting,
    paginatorInfo,
    refetch,
    searchControl,
  } as const;
}
